/**
 * Builders for every request body this feature sends. All curated-field
 * bodies go through toVideoInput, an explicit whitelist of StaffVideoInput,
 * so read-only fields the server owns (thumbnailUrl, durationSeconds,
 * slots, ...) can never be sent even if they are present on the
 * object passed in.
 */

/** Fields from StaffVideoInput, in Swagger order. */
export const VIDEO_INPUT_FIELDS = Object.freeze([
  'title',
  'description',
  'transcript',
  'resourceTitle',
  'resourceDescription',
  'resourceUrl',
  'isPublished',
  'audiences'
]);

const NULLABLE_TEXT_FIELDS = [
  'description',
  'transcript',
  'resourceTitle',
  'resourceDescription',
  'resourceUrl'
];

function toNullableText(value) {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Intent tag ids, unique and in the order given. Accepts ids or IntentTag
 * objects ({ id, name }) so a server video can be passed straight in.
 */
function toTagIds(value) {
  if (!Array.isArray(value)) return [];
  const ids = value
    .map((item) => Number(item && typeof item === 'object' ? item.id : item))
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)];
}

/**
 * The curated fields the server accepts on create/update (Swagger
 * StaffVideoInput, additionalProperties: false). The server rejects
 * thumbnailUrl with a 400. Home slots and playlists are not accepted here.
 *
 * @typedef {Object} StaffVideoInput
 * @property {string} [title]
 * @property {string|null} [description]
 * @property {string|null} [transcript]
 * @property {string|null} [resourceTitle]
 * @property {string|null} [resourceDescription]
 * @property {string|null} [resourceUrl]
 * @property {boolean} [isPublished]
 * @property {number[]} [audiences] - Intent tag ids (Educator and/or Partner)
 */

/**
 * @param {Object} values - form values (may contain extra keys, which are dropped)
 * @returns {StaffVideoInput}
 */
export function toVideoInput(values) {
  const input = {};

  VIDEO_INPUT_FIELDS.forEach((field) => {
    if (!Object.prototype.hasOwnProperty.call(values, field)) return;

    if (field === 'title') {
      input.title = String(values.title ?? '').trim();
    } else if (field === 'audiences') {
      input.audiences = toTagIds(values.audiences);
    } else if (field === 'isPublished') {
      input.isPublished = Boolean(values.isPublished);
    } else if (NULLABLE_TEXT_FIELDS.includes(field)) {
      input[field] = toNullableText(values[field]);
    }
  });

  return input;
}

/**
 * Body for POST /staff/videos. Uses the youtubeVideoId the prefill call
 * resolved (the API takes url OR youtubeVideoId, not both).
 *
 * @param {string} youtubeVideoId
 * @param {Object} values
 */
export function buildCreateVideoBody(youtubeVideoId, values) {
  return { youtubeVideoId, ...toVideoInput(values) };
}

/** Body for PATCH /staff/videos/:id. */
export function buildUpdateVideoBody(values) {
  return toVideoInput(values);
}

/** Body for POST /staff/videos/prefill and the playlist import calls. */
export function buildUrlBody(url) {
  return { url: String(url ?? '').trim() };
}

/**
 * Body for PUT /staff/videos/home-order: one audience's full ordered set.
 *
 * @param {import('@/features/videos-common/audiences').HomeVideoAudience} audience
 * @param {number[]} videoIds
 */
export function buildHomeOrderBody(audience, videoIds) {
  return { audience, videoIds: videoIds.map(Number) };
}

/**
 * Body for the playlist import and import-preview calls. audience is sent
 * only when one is chosen; omitting it imports as unassigned.
 *
 * @param {string} url
 * @param {import('@/features/videos-common/audiences').HomeVideoAudience|null} [audience]
 */
export function buildPlaylistImportBody(url, audience = null) {
  return audience ? { ...buildUrlBody(url), audience } : buildUrlBody(url);
}

/**
 * Body for PATCH /staff/playlists/:id changing only the audience. null
 * unassigns; replaceExisting takes the audience from its current holder.
 *
 * @param {import('@/features/videos-common/audiences').HomeVideoAudience|null} audience
 * @param {{ replaceExisting?: boolean }} [options]
 */
export function buildPlaylistAudienceBody(audience, options = {}) {
  const body = { audience: audience || null };
  if (options.replaceExisting) body.replaceExisting = true;
  return body;
}
